import { collection, doc, getDoc, getDocs, query, orderBy, limit, onSnapshot, where, getCountFromServer } from 'firebase/firestore';
import { db } from './../firebase'; // Assuming db is your Firestore instance
import { normalizeCompanyIds } from '../common/wasteReceivers.js';

const getStringIds = (value) => {
    if (!Array.isArray(value)) return [];

    return [
        ...new Set(
            value
                .filter((item) => typeof item === 'string' && item.trim())
                .map((item) => item.trim()),
        ),
    ];
};

const getUserWasteReceiverIds = (userData) => ([
    ...new Set([
        ...getStringIds(userData?.waste_receivers),
        ...getStringIds(userData?.wasteReceivers),
    ]),
]);

const getAccessibleCompanyIdsFromWasteReceivers = async (userData) => {
    const wasteReceiverIds = getUserWasteReceiverIds(userData);
    if (wasteReceiverIds.length === 0) return [];

    const receiverDocs = await Promise.all(
        wasteReceiverIds.map(async (receiverId) => {
            try {
                const receiverSnap = await getDoc(doc(db, 'waste_receivers', receiverId));
                return receiverSnap.exists() ? receiverSnap.data() : null;
            } catch (error) {
                console.error(`Error loading waste receiver ${receiverId}:`, error);
                return null;
            }
        }),
    );

    const companyIds = new Set();
    receiverDocs.forEach((receiverDoc) => {
        normalizeCompanyIds(receiverDoc?.companies || []).forEach((companyId) => {
            companyIds.add(companyId);
        });
    });

    return Array.from(companyIds);
};

// An admin is not assigned companies or waste receivers the way a normal user is: they oversee
// every one of them, and the Firestore rules already let them read every company. So instead of
// resolving what they have been granted, list what exists.
export const isAdminUser = (userData) => userData?.isAdmin === true;

const getAllDocumentIds = async (collectionPath) => {
    const snapshot = await getDocs(collection(db, collectionPath));
    return snapshot.docs.map((document) => document.id);
};

export const getAccessibleCompanyIds = async (userData) => {
    if (isAdminUser(userData)) return getAllDocumentIds('companies');

    const companyIds = new Set(normalizeCompanyIds(userData?.companies || []));

    const wasteReceiverCompanyIds = await getAccessibleCompanyIdsFromWasteReceivers(userData);
    wasteReceiverCompanyIds.forEach((companyId) => companyIds.add(companyId));

    return Array.from(companyIds);
};

export const getAccessibleWasteReceiverIds = async (userData) => {
    if (isAdminUser(userData)) return getAllDocumentIds('waste_receivers');

    return getUserWasteReceiverIds(userData);
};

export const getUnitsCount = (location) => {
    const count = (location?.display_units?.length ?? location?.units?.length) ?? 0;
    return count
}

// Function to get the last log of multiple units
export const getUnitsLastLog = async (unit_ids) => {
    const logs = [];
    for (const unit_id of unit_ids) {
        const collRef = collection(db, `units/${unit_id}/state-logs`);
        const q = query(collRef, orderBy("timestamp", "desc"), limit(1));
        console.log("Firebase call")
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
            if (doc.exists()) {
                logs.push(doc.data());
            }
        });
    }
    return logs;
};

// Function to set up a listener for unit state logs
export const getUnitStateListener = (unit_id, state_sub) => {
    const collRef = collection(db, `units/${unit_id}/state-logs`);
    const q = query(collRef, orderBy("timestamp", "desc"), limit(1));
    const unsubscribe = onSnapshot(q, 
        (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    try {
                        state_sub.next(change.doc.data());
                    } catch (error) {
                        console.error("Error parsing unit state logs: ", error);
                    }
                }
            });
        },
        (error) => {
            console.error("Error getting unit state logs: ", error);
        }
    );
    return unsubscribe;
};

export const getUnitRecordsListener = (unit_id, records_subscriber) => {
    const collRef = collection(db, `units/${unit_id}/inference-records`);
    const q = query(collRef, 
        where("is_test", "!=", true),
        orderBy("timestamp", "desc"), 
        limit(1)
    );
    const unsubscribe = onSnapshot(q,
        (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    try {
                        records_subscriber.next(change.doc.data());
                    } catch (error) {
                        console.error("Error parsing unit state logs: ", error);
                    }
                }
            });
        },
        (error) => {
            console.error("Error getting unit record: ", error);
        }
    );
    return unsubscribe;
};

export const getUnitDataListener = (unit_id, unit_data_subscriber) => {
    const docRef = doc(db, 'units', unit_id);

    const unsubscribe = onSnapshot(
        docRef, 
        (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                unit_data_subscriber.next(data);
            }
        },
        (error) => {
            console.error("Error getting unit data: ", error);
            unit_data_subscriber.error(error);
        }
    );

    return unsubscribe;
};

// Function to get the count of unit activations today
export const getUnitActivationsTodayCount = async (path) => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);

    const collInf = collection(db, path);
    const q = query(
        collInf, 
        where("timestamp", ">", midnight.getTime()),
        where("is_test", "!=", true)
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
};

// Function to get the latest use of a unit
export const getLatestUse = async (path) => {
    const collInf = collection(db, path);
    let output = '';

    const q2 = query(collInf, orderBy("timestamp", "desc"), limit(1));
    console.log("Firebase call")
    const querySnapshot = await getDocs(q2);
    querySnapshot.forEach((doc) => {
        if (doc.exists()) {
            const diff = Date.now() - doc.data()['timestamp'];

            const seconds = Math.floor(diff / 1000);
            const minutes = Math.floor(diff / 60000);
            const hours = Math.floor(diff / 3600000);
            const days = Math.floor(diff / 86400000);
            const weeks = Math.floor(diff / 604800000);
            const months = Math.floor(diff / 2629746000);
            const years = Math.floor(diff / 31556952000);

            if (seconds < 60) {
                output = `${seconds} sekunder siden`;
            } else if (minutes < 60) {
                output = `${minutes} minutter siden`;
            } else if (hours < 24) {
                output = hours === 1 ? '1 time siden' : `${hours} timer siden`;
            } else if (days < 7) {
                output = days === 1 ? '1 dag siden' : `${days} dage siden`;
            } else if (weeks < 4.348) { // Approximate weeks in a month
                output = weeks === 1 ? '1 uge siden' : `${weeks} uger siden`;
            } else if (months < 12) {
                output = months === 1 ? '1 måned siden' : `${months} måneder siden`;
            } else {
                output = years === 1 ? '1 år siden' : `${years} år siden`;
            }
        }
    });

    return output;
};

export const getAvailableLocations = async (userData) => {
    try {
        let locations = [];

        const locationCompanies = userData?.location_companies || {};
        for (let locationCompanyKey in locationCompanies) {
            const locationCompany = locationCompanies[locationCompanyKey];
            const companyId = locationCompany["company"];
            const location_id = locationCompany["location"];
            const locationDocRef = doc(db, `companies/${companyId}/locations`, location_id);
            const locationDoc = await getDoc(locationDocRef);
            if (locationDoc.exists()) {
                locations.push({
                    ...locationDoc.data(),
                    id: locationDoc.id,
                    company: companyId,
                });
            } else {
                console.log("No such document!");
            }
        }

        const accessibleCompanyIds = await getAccessibleCompanyIds(userData);

        // Read every company's locations subcollection at once rather than one after another:
        // an admin has every company in this list, which is a long wait in sequence.
        const companyLocationLists = await Promise.all(accessibleCompanyIds.map(async (companyId) => {
            const collectionRef = collection(db, `companies/${companyId}/locations`);
            console.log("Firebase call")
            const querySnapshot = await getDocs(query(collectionRef));
            return querySnapshot.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id,
                company: companyId,
            }));
        }));

        for (const companyLocations of companyLocationLists) {
            locations = locations.concat(companyLocations);

            //Put the locations with 0 units last in the list
            locations.sort((a, b) => {
                const aHasUnits = getUnitsCount(a) > 0;
                const bHasUnits = getUnitsCount(b) > 0;
                if (!aHasUnits && bHasUnits) {
                    return 1;  // b comes before a
                }
                
                return -1; // keep original order otherwise
            });
        }

        // A location reachable both through location_companies and through its company is the
        // same location twice, which an admin hits for every location they are also assigned.
        return [
            ...new Map(locations.map((location) => [`${location.company}/${location.id}`, location])).values(),
        ];
    } catch (error) {
        console.error("Error fetching available locations: ", error);
        throw error; // Propagate the error so the caller knows something went wrong
    }
};

// Function to fetch records from a single unit range
// Fetches records where timestamp >= start_date and optionally timestamp <= end_date (inclusive range)
// If current is true, fetches all records from start_date onwards (no end_date)
export const getRecordsFromUnitRange = async (unit_id, start_date, end_date, current = false) => {
    // Validate inputs to prevent undefined values in Firestore queries
    if (!unit_id || start_date === undefined || start_date === null) {
        console.warn(`Invalid parameters for getRecordsFromUnitRange: unit_id=${unit_id}, start_date=${start_date}`);
        return [];
    }

    // If not current, end_date is required
    if (!current && (end_date === undefined || end_date === null)) {
        console.warn(`Invalid parameters for getRecordsFromUnitRange: end_date is required when current is false, end_date=${end_date}`);
        return [];
    }

    try {
        console.log("getRecordsFromUnitRange unit_id", unit_id);
        console.log("getRecordsFromUnitRange start_date", start_date);
        console.log("getRecordsFromUnitRange end_date", end_date);
        console.log("getRecordsFromUnitRange current", current);
        const collRef = collection(db, `units/${unit_id}/inference-records`);
        
        // Build query based on whether it's a current range
        let q;
        if (current) {
            // Current range: only filter by start_date (no end_date)
            q = query(
                collRef,
                where("timestamp", ">=", start_date),
                orderBy("timestamp", "desc")
            );
        } else {
            // Regular range: filter by both start_date and end_date
            q = query(
                collRef,
                where("timestamp", ">=", start_date),
                where("timestamp", "<=", end_date),
                orderBy("timestamp", "desc")
            );
        }
        
        console.log("Firebase call")
        const querySnapshot = await getDocs(q);
        const output = querySnapshot.docs.map(doc => ({
            ...doc.data(),
            record_id: doc.id
        })).filter(record => record.is_test !== true);

        console.log("output", output);
        return output;
    } catch (error) {
        console.error(`Error fetching records for unit ${unit_id}:`, error);
        return [];
    }
};

// Function to fetch all records from unit_ranges for a display_unit
export const getRecordsFromDisplayUnit = async (display_unit) => {
    console.log("getRecordsFromDisplayUnit display_unit", display_unit);
    if (!display_unit.unit_ranges || !Array.isArray(display_unit.unit_ranges)) {
        return [];
    }

    const allRecords = [];
    
    // Filter out invalid ranges
    // A range is valid if:
    // - It has unit_id and start_date
    // - If current is not true, it must have end_date
    // - If current is true, end_date is not required
    const validRanges = display_unit.unit_ranges.filter(range => {
        if (!range || !range.unit_id || range.start_date === undefined || range.start_date === null) {
            return false;
        }
        
        // If current is true, end_date is not required
        if (range.current === true) {
            return true;
        }
        
        // If current is false or null/undefined, end_date is required
        return range.end_date !== undefined && range.end_date !== null;
    });
    
    if (validRanges.length === 0) {
        console.warn("No valid unit_ranges found in display_unit");
        return [];
    }
    
    console.log("validRanges", validRanges);
    const recordPromises = validRanges.map(range => {
        const isCurrent = range.current === true;
        // For current ranges, pass null as end_date (it won't be used)
        return getRecordsFromUnitRange(range.unit_id, range.start_date, range.end_date || null, isCurrent);
    });
    
    const recordArrays = await Promise.all(recordPromises);
    
    // Combine all records into one array
    recordArrays.forEach(records => {
        allRecords.push(...records);
    });
    
    // Sort by timestamp descending
    allRecords.sort((a, b) => b.timestamp - a.timestamp);
    
    return allRecords;
};

// Function to calculate activations today from a list of records
export const getActivationsTodayFromRecords = (records) => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const midnightTime = midnight.getTime();
    
    return records.filter(record => record.timestamp >= midnightTime).length;
};

// Function to calculate latest use from a list of records
export const getLatestUseFromRecords = (records) => {
    if (!records || records.length === 0) {
        return '';
    }
    
    // Records are already sorted by timestamp descending, so first one is latest
    const latestRecord = records[0];
    const diff = Date.now() - latestRecord.timestamp;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(diff / 604800000);
    const months = Math.floor(diff / 2629746000);
    const years = Math.floor(diff / 31556952000);

    if (seconds < 60) {
        return `${seconds} sekunder siden`;
    } else if (minutes < 60) {
        return `${minutes} minutter siden`;
    } else if (hours < 24) {
        return hours === 1 ? '1 time siden' : `${hours} timer siden`;
    } else if (days < 7) {
        return days === 1 ? '1 dag siden' : `${days} dage siden`;
    } else if (weeks < 4.348) { // Approximate weeks in a month
        return weeks === 1 ? '1 uge siden' : `${weeks} uger siden`;
    } else if (months < 12) {
        return months === 1 ? '1 måned siden' : `${months} måneder siden`;
    } else {
        return years === 1 ? '1 år siden' : `${years} år siden`;
    }
};

export const getWarnings = async (locations) => {
    const warnings = [];
    const max_days_since_last_use_before_warning = 2;
    const twoDaysAgo = Date.now() - (max_days_since_last_use_before_warning * 24 * 60 * 60 * 1000);
    const status_warning_threshold_hours = 1
    const status_warning_threshold = Date.now() - (status_warning_threshold_hours * 60 * 60 * 1000);

    try {
        // Get all units
        const unitsRef = collection(db, 'units');
        const unitsSnapshot = await getDocs(unitsRef);

        for (const unitDoc of unitsSnapshot.docs) {
            var lastStateWasBeforeThreshold = false;
            var noStateFound = false;

            const unitId = unitDoc.id;

            const productionLocations = locations.filter(location => location.id != "w6oLnRVe3jIZytaEBCkW");
            const unitIsDeployed = productionLocations.some(location => location.units.includes(unitId));
            
            // Check inference records
            const inferenceRef = collection(db, `units/${unitId}/inference-records`);
            const inferenceQuery = query(inferenceRef, orderBy("timestamp", "desc"), limit(1));
            const inferenceSnapshot = await getDocs(inferenceQuery);

            if (unitIsDeployed) {
                var lastInferenceWasBeforeThreshold = false;
                var noInferenceFound = false;
                var daysSinceLastUse = null;
                
                if (!inferenceSnapshot.empty) {
                    const latestRecord = inferenceSnapshot.docs[0].data();
                    const lastTimestamp = latestRecord.timestamp;

                    if (lastTimestamp < twoDaysAgo) {
                        lastInferenceWasBeforeThreshold = true;
                        // Calculate actual days since last use
                        const millisecondsSinceLastUse = Date.now() - lastTimestamp;
                        daysSinceLastUse = Math.floor(millisecondsSinceLastUse / (24 * 60 * 60 * 1000));
                    }
                } else {
                    noInferenceFound = true;
                }

                if ((lastInferenceWasBeforeThreshold || noInferenceFound)) {
                    // Show actual days if we have a timestamp, otherwise show threshold
                    const daysToShow = daysSinceLastUse !== null ? daysSinceLastUse : max_days_since_last_use_before_warning;
                    const warning_string = `Enhed ${unitId} har ikke været i brug i ${daysToShow} dage`;
                    warnings.push(warning_string);
                }
            }
            

            // Check state logs
            const stateLogsRef = collection(db, `units/${unitId}/state-logs`);
            const stateLogsQuery = query(stateLogsRef, orderBy("timestamp", "desc"), limit(1));
            const stateLogsSnapshot = await getDocs(stateLogsQuery);

            var hoursSinceLastStatus = null;

            if (!stateLogsSnapshot.empty) {
                const latestStateLog = stateLogsSnapshot.docs[0].data();
                const lastStateTimestamp = latestStateLog.timestamp;

                // Check if last state log is older than 1 hour
                if (lastStateTimestamp < status_warning_threshold) {
                    lastStateWasBeforeThreshold = true;
                    // Calculate actual hours since last status
                    const millisecondsSinceLastStatus = Date.now() - lastStateTimestamp;
                    hoursSinceLastStatus = Math.floor(millisecondsSinceLastStatus / (60 * 60 * 1000));
                }

                if (latestStateLog.state === "Error") {
                    const warning_string = `Enhed ${unitId} har haft en fejlstatus`;
                    warnings.push(warning_string);
                }
            } else {
                noStateFound = true;
            }

            if (unitIsDeployed && (lastStateWasBeforeThreshold || noStateFound)) {
                // Show actual hours if we have a timestamp, otherwise show threshold
                const hoursToShow = hoursSinceLastStatus !== null ? hoursSinceLastStatus : status_warning_threshold_hours;
                const warning_string = `Enhed ${unitId} har ikke sendt status i ${hoursToShow} time${hoursToShow === 1 ? '' : 'r'}`;
                warnings.push(warning_string);
            }
        }

        return warnings;
    } catch (error) {
        console.error("Error fetching warnings: ", error);
        throw error;
    }
};
